import React, { useState } from "react";
import { Form, Dimmer, Loader } from "semantic-ui-react";
import { useFormik } from "formik";
import { Auth, User } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./LoginForm.form";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./LoginFormPartner.scss";

const authController = new Auth();
const userController = new User();

const EDUCA360_ROLES = new Set([
  "admin",
  "partner",
  "institution",
  "teacher",
  "student",
  "user",
]);
const REVI_URL = "https://educa360revi.web.app";

function decodeTokenRole(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.role;
  } catch {
    return null;
  }
}

export function LoginFormPartner() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("auth");

  // Fase 1A M4 - K12 Free Refactor: free y pagado entran a /teacher/salas.
  const getPortalPath = (user) => {
    const { role } = user;
    if (role === "teacher") {
      return "teacher/salas";
    }
    return "";
  };

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValues) => {
      try {
        setLoading(true);
        const result = await authController.login(formValues);
        const data = result.data ?? result;
        const accessToken = data.access;
        const refreshToken = data.refresh;

        if (!accessToken || !refreshToken) {
          throw new Error(t("errors.loginInvalidResponse"));
        }

        const role = decodeTokenRole(accessToken);
        if (role && !EDUCA360_ROLES.has(role)) {
          authController.removeTokens();
          setLoading(false);
          toast.warning(
            `Esta cuenta pertenece a REVI. Accede desde ${REVI_URL}`,
            { position: "top-center", autoClose: 6000 },
          );
          return;
        }

        authController.setAccessToken(accessToken);
        authController.setRefreshToken(refreshToken);

        const userData = await userController.getMe(accessToken);
        await login(accessToken, userData);

        const portalPath = getPortalPath(userData);
        navigate(`/${portalPath}`, { replace: true });
      } catch (error) {
        setLoading(false);
        toast.error(error.message || t("errors.loginFailed"), {
          position: "top-center",
          autoClose: false,
        });
      }
    },
  });

  return (
    <>
      <ToastContainer />
      {loading && (
        <Dimmer active inverted page>
          <Loader content={t("common:actions.loading")} />
        </Dimmer>
      )}
      <Form onSubmit={formik.handleSubmit}>
        <Form.Input
          type="text"
          icon="user"
          iconPosition="left"
          name="email"
          placeholder={t("login.emailPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.email}
          error={formik.errors.email}
        />
        <Form.Input
          type="password"
          icon="lock"
          iconPosition="left"
          name="password"
          placeholder={t("login.passwordPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.password}
          error={formik.errors.password}
        />
        <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
          {t("login.submitButton")}
        </Form.Button>
        {/* Enlace para recuperar contraseña */}
        <div className="forgot-link">
          <Link to="/recuperar-contraseña" className="link-btn">
            {t("login.forgotPassword")}
          </Link>
        </div>
      </Form>
    </>
  );
}
